import { getDisplayLogoUrl } from '@/lib/logoUrl';
import {
  buildAutoDiscoveredLogoProvenanceText,
  type DiscoveredLogoCandidate,
} from '@/lib/logoProvenance';

export type CanonicalLogoSource = 'manual' | 'discovered' | null;

export interface CanonicalLogoResult {
  rawUrl: string | null;
  displayUrl: string | null;
  source: CanonicalLogoSource;
  manualUrl: string | null;
  discoveredUrl: string | null;
  provenanceText: string;
}

interface CanonicalLogoInput {
  manualLogoUrl?: string | null;
  manualLogoPath?: string | null;
  manualLogoBucket?: string | null;
  discoveredLogo?: DiscoveredLogoCandidate | null;
  domain?: string | null;
  websiteUrl?: string | null;
  cacheBustKey?: string | null;
}

function cleanLogoUrl(value?: string | null): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function resolveCanonicalLogo({
  manualLogoUrl,
  manualLogoPath,
  manualLogoBucket,
  discoveredLogo,
  domain,
  websiteUrl,
  cacheBustKey,
}: CanonicalLogoInput): CanonicalLogoResult {
  const manualUrl = cleanLogoUrl(manualLogoUrl);
  const manualPath = cleanLogoUrl(manualLogoPath);
  const hasManualPath = Boolean(manualPath && cleanLogoUrl(manualLogoBucket));
  const hasManualLogo = Boolean(manualUrl || hasManualPath);
  const discoveredUrl = cleanLogoUrl(discoveredLogo?.url);
  const rawUrl = manualUrl || (!hasManualLogo ? discoveredUrl : null);
  const source: CanonicalLogoSource = hasManualLogo ? 'manual' : discoveredUrl ? 'discovered' : null;

  return {
    rawUrl,
    displayUrl: getDisplayLogoUrl({
      primaryPath: hasManualPath ? manualPath : null,
      primaryBucket: hasManualPath ? manualLogoBucket : null,
      primaryUrl: rawUrl,
      domain,
      websiteUrl,
      cacheBustKey,
    }),
    source,
    manualUrl,
    discoveredUrl,
    provenanceText: discoveredLogo
      ? buildAutoDiscoveredLogoProvenanceText(discoveredLogo)
      : 'No discovered logo available',
  };
}
